export type TenantType = 'pool' | 'silo'
export type CalculationMethod = 'cost_plus' | 'margin'
export type PriceRoundingMethod =
  | 'no_rounding'
  | 'round_integer'
  | 'round_nearest_5'
  | 'round_nearest_10'
export type ComponentType = 'product' | 'semi_finished' | 'raw_material' | 'packaging' | 'trading_good'

export interface Tenant {
  id: string
  name: string
  slug: string
  tenant_type: TenantType
  silo_database_url?: string | null
  default_calculation_method: CalculationMethod
  price_rounding_method?: PriceRoundingMethod
  logo_data?: string | null
  is_active?: boolean
  created_at?: string
}

export interface Category {
  id: string
  tenant_id: string
  name: string
  description?: string | null
  created_at?: string
}

export interface ComponentItem {
  id: string
  tenant_id: string
  parent_type: 'product' | 'semi_finished'
  parent_product_id?: string | null
  parent_semi_finished_id?: string | null
  component_type: ComponentType
  component_id: string
  component_trading_good_id?: string | null
  code: string
  name: string
  unit: string
  birim_maliyet: number | string
  unit_amount: number | string
  quantity: number | string
  recipe_amount?: number | string | null
  calculated_genel_gider?: number | string | null
  created_at?: string
}

export type ProductComponent = ComponentItem

export interface Product {
  id: string
  tenant_id: string
  category_id?: string | null
  code: string
  name: string
  description?: string | null
  image?: string | null
  unit?: string
  barcode?: string | null
  alternatives_barcodes?: string[]
  kdv_rate?: string | number
  maliyet?: string | number
  recipe_output_quantity?: number | string
  calculation_method_override?: CalculationMethod | null
  created_at?: string
  category?: Category | null
  components?: ProductComponent[]
}

export interface TradingGood {
  id: string
  tenant_id: string
  category_id?: string | null
  code: string
  name: string
  description?: string | null
  image?: string | null
  unit?: string
  barcode?: string | null
  alternatives_barcodes?: string[]
  kdv_rate?: string | number
  maliyet: string | number
  calculation_method_override?: CalculationMethod | null
  created_at?: string
  category?: Category | null
}

export interface RawMaterial {
  id: string
  tenant_id: string
  category_id?: string | null
  code: string
  name: string
  description?: string | null
  unit: string
  barcode?: string | null
  maliyet: string | number
  created_at?: string
  category?: Category | null
}

export interface RawMaterialCostHistory {
  id: string
  tenant_id: string
  raw_material_id: string
  maliyet: string | number
  created_at: string
}

export interface Packaging {
  id: string
  tenant_id: string
  category_id?: string | null
  code: string
  name: string
  description?: string | null
  unit: string
  barcode?: string | null
  maliyet: string | number
  created_at?: string
  category?: Category | null
}

export interface SemiFinishedProduct {
  id: string
  tenant_id: string
  category_id?: string | null
  code: string
  name: string
  description?: string | null
  image?: string | null
  unit: string
  barcode?: string | null
  maliyet?: string | number
  recipe_output_quantity?: number | string
  created_at?: string
  category?: Category | null
  components?: ProductComponent[]
}

export type SalesChannelParameterType = 'multiplier' | 'profit_margin'

export interface SalesChannelParameterCategoryValue {
  id?: string
  category_id: string
  category_name?: string
  value: number | string
}

export interface SalesChannelParameter {
  id?: string
  parameter_type: SalesChannelParameterType
  label: string
  condition_type?: 'none' | 'category'
  value?: number | string | null
  order_index: number
  category_values?: SalesChannelParameterCategoryValue[]
}

export const SUPPORTED_CURRENCIES = ['TL', 'USD', 'EUR'] as const
export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number]
export const CURRENCY_SYMBOLS: Record<string, string> = { TL: '₺', USD: '$', EUR: '€' }
export const CURRENCY_LABELS: Record<string, string> = {
  TL: 'Türk Lirası (₺)',
  USD: 'Amerikan Doları ($)',
  EUR: 'Euro (€)',
}

export interface SalesChannel {
  id: string
  tenant_id: string
  name: string
  code?: string | null
  contact_email?: string | null
  preferred_currency: string
  exchange_rate_overrides?: Record<string, number | null>
  effective_exchange_rates?: Record<string, number>
  base_sales_channel_id?: string | null
  base_sales_channel_name?: string | null
  include_kdv?: boolean
  parameters: SalesChannelParameter[]
  created_at?: string
  updated_at?: string
}

export interface ComponentCalculationItem {
  component_id: string
  component_type: ComponentType
  type_label: string
  code: string
  name: string
  unit: string
  birim_maliyet: number | string
  birim_miktar: number | string
  toplam_miktar: number | string
  isletme_katsayisi_orani?: number | string
  taban_maliyet: number | string
  genel_gider: number | string
  sub_components?: ComponentCalculationItem[]
}

export interface ParameterStepBreakdown {
  id?: string
  parameter_type: SalesChannelParameterType
  label: string
  applied_value: number | string
  price_before: number | string
  price_after: number | string
  is_overridden?: boolean
}

export interface SalesChannelCalculationInfo {
  sales_channel_id?: string
  sales_channel_name?: string
  parameters?: ParameterStepBreakdown[]
}

export interface PriceCalculationBreakdown {
  item_type?: string
  miktar: number | string
  birim_maliyet: number | string
  taban_maliyet_toplami: number | string
  bilesenler: ComponentCalculationItem[]
  bilesenler_toplami: number | string
  toplam_maliyet: number | string
  alis_fiyati?: number | string
  parameter_steps?: ParameterStepBreakdown[]
  hesaplama_yontemi: string
  kdv_haric_satis_fiyati: number | string
  kdv_dahil_satis_fiyati?: number | string | null
  final_satis_fiyati: number | string
  satis_fiyati?: number | string
  birim_son_satis_fiyati?: number | string
  satis_kanali?: SalesChannelCalculationInfo | null
  toplam_kar?: number | string
  kdv_orani?: number | string | null
  kdv_tutari?: number | string | null
}

export interface CalculationResponse {
  success: boolean
  breakdown: PriceCalculationBreakdown
}

export interface PriceListProfitMargin {
  parameter_id: string
  label: string
  value?: number | string | null
  default_value?: number | string | null
  is_overridden: boolean
  order_index: number
  sales_channel_id?: string
  sales_channel_name?: string
  is_applicable?: boolean
}

export interface ChannelStepPrice {
  sales_channel_id: string
  sales_channel_name: string
  profit_margins: PriceListProfitMargin[]
  kdv_haric_satis_fiyati: number | string
}

export interface PriceListRow {
  item_type: 'product' | 'trading_good' | string
  product_id: string
  product_name: string
  product_code: string
  category_id?: string | null
  category_name?: string | null
  alis_fiyati?: number | string
  maliyet: number | string
  genel_maliyet?: number | string
  channel_steps?: ChannelStepPrice[]
  profit_margins: PriceListProfitMargin[]
  toplam_kar?: number | string | null
  calculation_method?: CalculationMethod | string
  kdv_haric_satis_fiyati: number | string
  kdv_dahil_satis_fiyati?: number | string | null
  final_satis_fiyati: number | string
  satis_fiyati?: number | string
  kdv_rate?: number | string | null
  unit: string
}

export interface BulkProductUpdatePayload {
  product_ids: string[]
  field: string
  unit_value?: string
  category_id_value?: string | null
  kdv_rate_value?: number
  maliyet_value?: number
}

export interface User {
  id: string
  tenant_id: string
  email: string
  full_name?: string | null
  role: 'tenant_admin' | 'tenant_user' | string
  is_active: boolean
  created_at?: string
}

export interface TenantUserCreatePayload {
  email: string
  password: string
  full_name?: string
  role?: string
}

export interface TenantUserUpdatePayload {
  email?: string
  password?: string
  full_name?: string
  is_active?: boolean
  role?: string
}

export interface PlatformAdmin {
  id: string
  email: string
  full_name?: string | null
  is_active: boolean
  created_at?: string
}

export interface PlatformAuthResponse {
  access_token: string
  token_type: string
  admin: PlatformAdmin
}

export interface AuthResponse {
  access_token: string
  token_type: string
  user_id: string
  email: string
  role: string
  tenant: Tenant
}

export interface CurrentUserProfile {
  user: User
  tenant: Tenant
}

export type DemoComponentType = 'demo_product' | 'demo_semi_finished' | 'product' | 'semi_finished' | 'raw_material' | 'packaging' | 'trading_good'

export interface DemoComponentItem {
  id: string
  parent_type?: 'demo_product' | 'demo_semi_finished'
  parent_demo_product_id?: string | null
  parent_demo_semi_finished_id?: string | null
  component_type: DemoComponentType
  component_demo_product_id?: string | null
  component_demo_semi_finished_id?: string | null
  component_product_id?: string | null
  component_semi_finished_id?: string | null
  component_raw_material_id?: string | null
  component_packaging_id?: string | null
  component_trading_good_id?: string | null
  unit_amount: number | string
  recipe_amount?: number | string | null
  created_at?: string
  component_name?: string
  component_code?: string | null
  component_unit?: string | null
  component_cost?: number | string | null
}

export interface DemoProduct {
  id: string
  tenant_id?: string
  category_id?: string | null
  category_name?: string | null
  name: string
  description?: string | null
  image?: string | null
  unit?: string
  barcode?: string | null
  alternatives_barcodes?: string[]
  kdv_rate?: number | string
  maliyet?: number | string
  recipe_output_quantity?: number | string
  components_count?: number
  components?: DemoComponentItem[]
  created_at?: string
  updated_at?: string
}

export interface DemoSemiFinishedProduct {
  id: string
  tenant_id?: string
  category_id?: string | null
  category_name?: string | null
  name: string
  description?: string | null
  image?: string | null
  unit?: string
  barcode?: string | null
  maliyet?: number | string
  recipe_output_quantity?: number | string
  components_count?: number
  components?: DemoComponentItem[]
  created_at?: string
  updated_at?: string
}

export interface DemoPriceListRow {
  demo_product_id: string
  product_name: string
  category_id?: string | null
  category_name?: string | null
  maliyet: number | string
  alis_fiyati: number | string
  genel_maliyet?: number | string
  channel_steps?: ChannelStepPrice[]
  profit_margins: PriceListProfitMargin[]
  toplam_kar?: number | string | null
  calculation_method?: CalculationMethod | string
  kdv_haric_satis_fiyati: number | string
  kdv_dahil_satis_fiyati?: number | string | null
  final_satis_fiyati: number | string
  kdv_rate?: number | string | null
}

export type ActiveTab =
  | 'price-list'
  | 'products'
  | 'trading-goods'
  | 'semi-finished'
  | 'raw-materials'
  | 'packagings'
  | 'demo-products'
  | 'demo-semi-finished'
  | 'demo-price-list'
  | 'categories'
  | 'sales-channels'
  | 'settings'


